import { Calendar, ChevronDown, Package, Ship } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

import ScanForm from './components/ScanForm'
import ScanRecord from './components/ScanRecord'
import PalletIndex from './CreatePallet'
import ScanTabs from './ScanTabs'
import { useDevanningActions, useDevanningState } from './stores/devanningStore'
import { useScanProgressActions } from './stores/scanProgressStore'

const Inbound: React.FC = () => {
  const { masterBills, masterBill } = useDevanningState()
  const { loadMasterBills, setMasterBill } = useDevanningActions()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const { fetchProgress } = useScanProgressActions()

  const handleChange = (masterBillId: number) => {
    setMasterBill(masterBillId)
    setIsDropdownOpen(false)
    fetchProgress()
  }

  useEffect(() => {
    loadMasterBills()
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '未设置'
    return new Date(dateStr).toLocaleDateString('zh-CN')
  }

  return (
    <div className="max-w-7xl">
      {masterBills.length > 0 && (
        <div className="mb-4 relative" ref={dropdownRef}>
          <div
            className="bg-white border border-gray-300 rounded-lg p-3 cursor-pointer hover:border-blue-400 transition-colors"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                {masterBill ? (
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Ship className="w-4 h-4 text-blue-600" />
                      <span className="font-semibold text-lg">{masterBill.masterBlNumber}</span>
                      <span className="text-sm text-gray-500">({masterBill.vesselName || '船名未知'})</span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        <span>柜号: {masterBill.containerNumber || '未设置'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>拆柜日期: {formatDate(masterBill.devanDate)}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-500">请选择主提单</span>
                )}
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
              />
            </div>
          </div>

          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-10 max-h-60 overflow-y-auto">
              {masterBills.map((bill) => (
                <div
                  key={bill.id}
                  className={`p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                    masterBill?.id === bill.id ? 'bg-blue-50 border-blue-200' : ''
                  }`}
                  onClick={() => handleChange(bill.id)}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Ship className="w-4 h-4 text-blue-600" />
                    <span className="font-medium">{bill.masterBlNumber}</span>
                    <span className="text-sm text-gray-500">({bill.vesselName || '船名未知'})</span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span>柜号: {bill.containerNumber || '未设置'}</span>
                    <span>拆柜日期: {formatDate(bill.devanDate)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="bg-gray-50 flex flex-col">
        {/* 主要内容区域 - 响应式布局 */}
        <div className="flex-1 flex flex-col lg:flex-row gap-4 pb-4 min-h-0">
          {/* 左侧操作区 */}
          <div className="w-full lg:w-1/2 flex flex-col space-y-4">
            {/* 扫描区域 */}
            <div className="flex-shrink-0 space-y-4">
              <ScanForm />

              <ScanRecord />

              <PalletIndex />
            </div>
          </div>

          {/* 右侧信息展示区 */}
          <div className="w-full lg:w-1/2 flex flex-col">
            {/* 扫描进度和记录的 Tab 组件 */}
            <div className="flex-1">
              <ScanTabs />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Inbound
